import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { getMonth, getYear } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaCalendarCheck, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const API_BASE_URL = "https://api.muktihospital.com/api";

const DoctorApptStatusChart = ({ user }) => {
    const [chartState, setChartState] = useState({ series: [], options: {} });
    const [breakdownData, setBreakdownData] = useState([]);
    const [totalAppointments, setTotalAppointments] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.email) {
            setLoading(false);
            console.log("ApptStatusChart: Waiting for user email...");
            return;
        }

        const fetchChartData = async () => {
            setLoading(true);
            const now = new Date();
            const year = getYear(now);
            const month = getMonth(now);

            try {
                const api = axios.create({ baseURL: API_BASE_URL, headers: { "x-api-key": API_KEY } });

                const [doctorsRes, appointmentsRes] = await Promise.all([
                    api.get(`/doctor`),
                    api.get(`/appointment`)
                ]);

                const allDoctors = doctorsRes.data?.data || doctorsRes.data || [];
                const allAppointments = appointmentsRes.data?.appointments || [];

                const myDoctorProfile = allDoctors.find(doc => doc.email === user.email);
                if (!myDoctorProfile) {
                    setLoading(false);
                    return;
                }
                
                const loggedInDoctorId = myDoctorProfile.id;
                const myAppointments = allAppointments.filter(app => app.doctorId === loggedInDoctorId);
                
                const myAppointmentsThisMonth = myAppointments.filter(app => {
                    if (!app.appointmentDate) return false;
                    const appDate = new Date(app.appointmentDate);
                    return appDate.getFullYear() === year && appDate.getMonth() === month;
                });

                const approvedCount = myAppointmentsThisMonth.filter(app => app.status === 'approved').length;
                const pendingCount = myAppointmentsThisMonth.filter(app => app.status === 'pending').length;
                const cancelledCount = myAppointmentsThisMonth.filter(app => app.status === 'cancelled').length;
                const totalCount = myAppointmentsThisMonth.length;
                
                setTotalAppointments(totalCount);

                const series = [approvedCount, pendingCount, cancelledCount];
                const labels = ['Approved', 'Pending', 'Cancelled'];
                const chartColors = ['#6AC75A', '#F59E0B', '#EF4444'];

                setChartState({
                    series: series,
                    options: {
                        chart: { type: 'donut' },
                        labels: labels,
                        colors: chartColors,
                        legend: { show: false },
                        dataLabels: { enabled: false },
                        plotOptions: {
                            pie: {
                                donut: {
                                    size: "75%",
                                    labels: {
                                        show: true,
                                        name: { show: false },
                                        value: { show: true, fontSize: '24px', fontWeight: 'bold', offsetY: 8 },
                                        total: {
                                            show: true,
                                            label: 'Total This Month',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#373d3f',
                                            formatter: () => totalCount.toLocaleString(),
                                        },
                                    },
                                },
                            },
                        },
                    },
                });

                setBreakdownData([
                    {
                        name: 'Approved',
                        count: approvedCount,
                        percentage: totalCount > 0 ? ((approvedCount / totalCount) * 100).toFixed(2) : "0.00",
                        icon: <FaCalendarCheck />,
                        color: 'text-green-500',
                        bgColor: 'bg-green-100',
                    },
                    {
                        name: 'Pending',
                        count: pendingCount,
                        percentage: totalCount > 0 ? ((pendingCount / totalCount) * 100).toFixed(2) : "0.00",
                        icon: <FaHourglassHalf />,
                        color: 'text-orange-500',
                        bgColor: 'bg-orange-100',
                    },
                    {
                        name: 'Cancelled',
                        count: cancelledCount,
                        percentage: totalCount > 0 ? ((cancelledCount / totalCount) * 100).toFixed(2) : "0.00",
                        icon: <FaTimesCircle />,
                        color: 'text-red-500',
                        bgColor: 'bg-red-100',
                    },
                ]);

            } catch (error) {
                console.error("Error fetching appointment status data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, [user]);

    if (loading) {
        return <Skeleton height={400} />;
    }
    
    if (totalAppointments === 0 && !loading) {
         return (
            <div className="flex items-center justify-center h-[400px] text-gray-500">
                No appointment data for this month.
            </div>
        );
    }

    return (
        <div>
            <div id="chart" className="dashboard-donut-chart">
                <ReactApexChart options={chartState.options} series={chartState.series} type="donut" height={220} />
            </div>

            <div className="mt-6 flex justify-center items-center gap-6 sm:gap-10">
                {breakdownData.map((item, index) => (
                    <div key={index} className="flex flex-col items-center text-center">
                        <p className="text-sm font-medium text-gray-500">{item.name}</p>
                        <div className={`flex items-center gap-2 my-1 ${item.color}`}>
                            <span className="text-2xl">{item.icon}</span>
                            <p className="text-3xl font-bold text-gray-800">{item.count}</p>
                        </div>
                        <div className={`${item.bgColor} ${item.color} text-xs font-bold px-2 py-1 rounded`}>
                            {item.percentage}%
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DoctorApptStatusChart;